/**
 * SINGLE SOURCE OF TRUTH — monthly website plans.
 * Public pricing, Build a Quote, CRM invoices, subscriptions, emails and
 * MRR fallbacks all read from here. Legacy labels/prices are kept only so
 * historical invoices and grandfathered subscriptions still resolve.
 */
export type MonthlyPlan = {
  key: 'hosting' | 'hosting_mgmt' | 'full_support'
  name: string
  dollars: number
  cents: number
  /** Invoice line label (price is parsed from it). */
  label: string
  /** Stripe price lookup key. */
  stripePriceId: string
  /** First-month-free credit line label. */
  creditLabel: string
  badge?: string
  features: string[]
}

export const MONTHLY_PLANS: MonthlyPlan[] = [
  {
    key: 'hosting', name: 'Hosting', dollars: 49, cents: 4900,
    label: 'Hosting ($49/month)', stripePriceId: 'mgmt_hosting_50_monthly',
    creditLabel: 'First Month Free Credit (-$49)',
    features: ['Website hosting', 'Security', 'Uptime monitoring', 'One small revision per month'],
  },
  {
    key: 'hosting_mgmt', name: 'Hosting + Management', dollars: 99, cents: 9900,
    label: 'Hosting + Management ($99/month)', stripePriceId: 'mgmt_hosting_mgmt_100_monthly',
    creditLabel: 'First Month Free Credit (-$99)', badge: 'MOST POPULAR',
    features: ['Everything in Hosting', 'CRM and form support', 'Backend maintenance', 'Payment-system support', 'Two small updates per month'],
  },
  {
    key: 'full_support', name: 'Full Support', dollars: 199, cents: 19900,
    label: 'Full Support ($199/month)', stripePriceId: 'mgmt_full_support_200_monthly',
    creditLabel: 'First Month Free Credit (-$199)',
    features: ['Everything in Hosting + Management', 'Priority support', 'Automation and integration support', 'Advanced troubleshooting'],
  },
]

export const PLAN_HOSTING = MONTHLY_PLANS[0]
export const PLAN_HOSTING_MGMT = MONTHLY_PLANS[1]
export const PLAN_FULL_SUPPORT = MONTHLY_PLANS[2]
export const MONTHLY_FROM_DOLLARS = PLAN_HOSTING.dollars

/** Current plan label → cents, plus credits. */
export const CURRENT_PLAN_PRICE_CENTS: Record<string, number> = Object.fromEntries(
  MONTHLY_PLANS.flatMap((p) => [[p.label, p.cents], [p.creditLabel, -p.cents]]),
)

/** Plan name (no price) → current cents; used as MRR fallback for new rows. */
export const PLAN_NAME_CENTS: Record<string, number> = {
  Hosting: 4900,
  'Hosting + Management': 9900,
  'Full Support': 19900,
  // legacy name for the top tier
  'Hosting, Management & Support': 19900,
}

/** Labels from older price lists — still valid on historical/grandfathered records. */
export const LEGACY_PLAN_LABELS = [
  'Hosting ($79/month)', 'Hosting + Management ($119/month)', 'Hosting, Management & Support ($219/month)',
  'Hosting ($49/month)', 'Hosting + Management ($99/month)', 'Full Support ($199/month)',
  'Hosting, Management & Support ($199/month)',
  'Basic Plan ($49/month)', 'Growth Plan ($99/month)', 'Pro Plan ($199/month)',
]

export function isLegacyPlanLabel(label: string | null | undefined): boolean {
  return !!label && LEGACY_PLAN_LABELS.includes(label)
}

/** True when a recurring amount doesn't match any current plan price. */
export function isLegacyPlanAmount(cents: number | null | undefined, label?: string | null): boolean {
  if (label && isLegacyPlanLabel(label)) return true
  if (cents == null) return false
  if (cents === 2900) return false // non-profit
  return !MONTHLY_PLANS.some((p) => p.cents === cents)
}
